import { SwappableDialogRef, SwappableScreens } from '@common/types/common';
import { SuccessDialogContent } from '@components/display/dialogs';
import { CloseButton } from '@components/utility/buttons';
import { Button, ButtonProps, Dialog, DialogActions, DialogProps, DialogTitle, Typography } from '@mui/material';
import { forwardRef, useImperativeHandle, useState } from 'react';

interface Btn extends Omit<ButtonProps, 'children'> {
    title: string;
}

interface GenericRemoveDialogProps extends Omit<DialogProps, 'children'> {
    /** The content to display as the dialog's title */
    titleSX: React.ReactElement;
    /** Props for the secondary button, which is usually the cancel or go back button */
    secondaryBtnProps: Btn;
    /** Props for the primary button, which is usually the confirm or remove button */
    primaryBtnProps: Btn;
    /** Description to display on the success screen */
    successDescription?: string;
}

const GenericRemoveDialog = forwardRef<
    SwappableDialogRef,
    Omit<GenericRemoveDialogProps, 'component' | 'components' | 'componentsProps'>
>(({ titleSX, primaryBtnProps, secondaryBtnProps, successDescription, ...props }, ref) => {
    const [screen, setScreen] = useState<SwappableScreens>('main');

    const swapScreen = (screenName: SwappableScreens) => setScreen(screenName);

    useImperativeHandle(ref, () => ({
        swapScreen,
    }));

    return (
        <Dialog {...props} disableEscapeKeyDown classes={{ paper: 'dialog-paper' }}>
            {screen === 'main' ? (
                <>
                    <CloseButton onClick={() => props.onClose?.({}, 'backdropClick')} />
                    <DialogTitle>{titleSX}</DialogTitle>
                    <DialogActions className="flex gap-5 mt-12">
                        <Button
                            fullWidth
                            color="secondary-alt"
                            className="rounded-full"
                            sx={{
                                border: '1px solid #00000029',
                            }}
                            {...secondaryBtnProps}
                        >
                            <Typography fontSize={15} fontWeight={500} letterSpacing={0.46} color="black.main">
                                {secondaryBtnProps.title}
                            </Typography>
                        </Button>
                        <Button
                            fullWidth
                            className="rounded-full"
                            sx={{
                                border: '1px solid #00000029',
                            }}
                            color="error"
                            {...primaryBtnProps}
                        >
                            <Typography fontSize={15} fontWeight={500} letterSpacing={0.46}>
                                {primaryBtnProps.title}
                            </Typography>
                        </Button>
                    </DialogActions>
                </>
            ) : (
                <SuccessDialogContent
                    description={successDescription}
                    btn={{
                        text: 'Continue',
                        onClick: () => {
                            setScreen('main');
                            props.onClose?.({}, 'backdropClick');
                        },
                    }}
                />
            )}
        </Dialog>
    );
});

export { GenericRemoveDialog };
export type { GenericRemoveDialogProps };
